import React, { useState, useEffect, useRef }  from "react";
import { Calendar, Calculator } from "lucide-react";
import { message } from "../../components/Common/SnakeAlert/SnakeAlert";
import graphqlRequest from "../../requestMethod";

import "./ShiftConfiguration.scss";

const pocOptions = [
  "Pratika - Head Nurse (pratika@hospital.com)",
  "Dr. Johnson - Department Head (johnson@hospital.com)",
  "Sarah Wilson - Shift Supervisor (sarah.wilson@hospital.com)",
  "Michael Chen - Charge Nurse (michael.chen@hospital.com)",
];

const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ShiftConfiguration = ({
  config,
  onChange,
  onGenerateShifts,
  isGenerating,
  minimumHour,
  maximumHour,
  professionalDetails,
}) => {
  const [fetchContactDetails, setFetchContactDetails] = useState([]);
  const [isGeneratedOnce, setIsGeneratedOnce] = useState(false);
  const [initialConfig, setInitialConfig] = useState({});
  const [initialProfessionalDetails, setInitialProfessionalDetails] = useState({});

  let loginData = localStorage.getItem("auth");
  let token = JSON.parse(loginData)?.accessToken;

  const now = new Date();
  const today = now.toISOString().split("T")[0];
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // console.log("config", config);

  const generateTimeOptions = () => {
    const options = [];
    try {
      const isToday = config.startDate === today;
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const timeInMinutes = hour * 60 + minute;
          // If it's today, only include future times (with 30-minute buffer)
          if (!isToday || timeInMinutes > currentTimeInMinutes) {
            const timeString = `${hour.toString().padStart(2, "0")}:${minute
              .toString()
              .padStart(2, "0")}`;
            options.push(timeString);
          }
        }
      }
      
      // If it's today and no future times are available (e.g., very late at night), add the first time slot for the next day
      if (isToday && options.length === 0) {
        options.push("00:00");
      }
    } catch (error) {
      console.error("Error generating time options:", error);
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  const updateConfig = (key, value) => {
    // When switching to multiple days, auto-select all days
    if (
      key === "shiftType" &&
      value === "multiple" &&
      config.shiftType === "single"
    ) {
      onChange({ ...config, [key]: value, recurringDays: dayLabels });
    } else if (key === "shiftType" && value === "single") {
      // When switching to single day, clear recurring days
      onChange({ ...config, [key]: value, recurringDays: [] });
    } else {
      onChange({ ...config, [key]: value });
    }
  };

  const toggleRecurringDay = (day, index) => {
     const allowedDays = getWeekdaysBetweenDates(
       config.startDate,
       config.endDate
     );

     // Check if selected day is within allowed days
     if (!allowedDays.includes(day)) {
       message.info(
         "Dates selected are not in sync with the recurring days. Please review dates and recurring days"
       );
       return;
     }
    const newDays = [...config.recurringDays];
    const dayIndex = newDays.indexOf(day);

    if (dayIndex > -1) {
      newDays.splice(dayIndex, 1);
    } else {
      newDays.push(day);
    }

    updateConfig("recurringDays", newDays);
  };

const calculateShiftSummary = () => {
  const professionalCount = config.professionalCount || 0;
  const rateOffered = config.rateOffered || 0;
  const duration = config.duration || 0;

  if (config.shiftType === "single") {
    const totalShifts = professionalCount;

    return {
      totalJobs: professionalCount,
      totalShifts: totalShifts,
      estimatedCost: totalShifts * rateOffered * duration,
    };
  }

  if (!config.startDate || !config.endDate) {
    return {
      totalJobs: 0,
      totalShifts: 0,
      estimatedCost: 0,
    };
  }

  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const startDate = new Date(config.startDate);
  const endDate = new Date(config.endDate);

  let matchedDays = 0;

  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const day = dayLabels[currentDate.getDay()];

    if (
      !config.recurringDays?.length || // if recurringDays is empty, assume 0 active days
      config.recurringDays.includes(day)
    ) {
      matchedDays++;
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  const totalShifts = matchedDays * professionalCount;

  return {
    totalJobs: totalShifts,
    totalShifts: totalShifts,
    estimatedCost: totalShifts * rateOffered * duration,
  };
};



  const summary = calculateShiftSummary();

  function fetchContactDetailsUser() {
    const query = `
       query{
         withAuth(token:"${token}"){
           listOrganizationUsers {
             id
             email
             contact
             name
           }
          }
        }
     `;
    graphqlRequest(query).then((response) => {
      // console.log("response", response.data.withAuth.listOrganizationUsers);
      setFetchContactDetails(response.data.withAuth.listOrganizationUsers);
    });
  }

  const getWeekdaysBetweenDates = (startDateStr, endDateStr) => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    const weekdaysSet = new Set();
    const dayMap = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; // JS day order

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      const day = dayMap[d.getDay()];
      weekdaysSet.add(day);
    }

    return Array.from(weekdaysSet); // e.g., ["Tue", "Wed", "Thu"]
  };

  useEffect(() => {
    fetchContactDetailsUser();
  }, []);

  useEffect(() => {
    if (config.shiftType === "multiple" && config.startDate && config.endDate) {
      const autoRecurringDays = getWeekdaysBetweenDates(
        config.startDate,
        config.endDate
      );
      updateConfig("recurringDays", autoRecurringDays);
    }
  }, [config.startDate, config.endDate, config.shiftType]);

  useEffect(() => {
    if (isGeneratedOnce) {
      const hasChanges =
        JSON.stringify(config) !== JSON.stringify(initialConfig) || JSON.stringify(professionalDetails) !== JSON.stringify(initialProfessionalDetails);
      if (hasChanges) {
        setIsGeneratedOnce(false);
      }
    }
  }, [config, isGeneratedOnce, initialConfig, professionalDetails, initialProfessionalDetails]); 

  // console.log("fetchContactDetails", fetchContactDetails);

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time part to compare dates only
    
    const selected = new Date(selectedDate);
    
    // If selected date is in the past, don't update the state
    if (selected < today) {
      message.error("Please select today's date or a future date");
      return;
    }
    
    updateConfig("startDate", selectedDate);
  };

  return (
    <div className="card">
      <div className="card-content">
        <h2 className="card-title">
          <span className="card-icon">
            {" "}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="28"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </span>
          Shift Scheduling
        </h2>

        <div className="grid-container">
          <div className="left-column">
            <div className="form-group">
              <label>Shift Type</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    value="single"
                    checked={config.shiftType === "single"}
                    onChange={() => updateConfig("shiftType", "single")}
                  />{" "}
                  1 Day Job
                </label>
                <label>
                  <input
                    type="radio"
                    value="multiple"
                    checked={config.shiftType === "multiple"}
                    onChange={() => updateConfig("shiftType", "multiple")}
                  />{" "}
                  Multiple Days Job
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>No of Professionals</label>
              <input
                type="number"
                //  min="1"
                placeholder="Enter number of professionals"
                value={config.professionalCount || ""}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || "";
                  if (value === "" || value >= 1)
                    updateConfig("professionalCount", value);
                }}
                onWheel={(e) => e.target.blur()}
                style={{
                  // backgroundColor: "#fafafa", // light grey
                  backgroundColor: "#fafbfc", // light blue
                }}
              />
            </div>

            <div className="form-group">
              <label>Rate Offered (per hour)</label>
              <div className="input-with-symbol">
                <span>₹</span>
                <input
                  placeholder="Enter Rate Offered (per hour)  "
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  value={config.rateOffered || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      updateConfig(
                        "rateOffered",
                        value === "" ? "" : parseFloat(value)
                      );
                    }
                  }}
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e" || e.key === "+") {
                      e.preventDefault(); // Block minus, exponent, and plus signs
                    }
                  }}
                  // onChange={(e) =>
                  //   updateConfig(
                  //     "rateOffered",
                  //     parseFloat(e.target.value) || ""
                  //   )
                  // }
                  style={{ paddingLeft: "1.5rem", backgroundColor: "#fafbfc" }}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Camp Duty</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    value="yes"
                    checked={config.campDuty === true}
                    onChange={() => updateConfig("campDuty", true)}
                  />{" "}
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    value="no"
                    checked={config.campDuty === false}
                    onChange={() => updateConfig("campDuty", false)}
                  />{" "}
                  No
                </label>
              </div>
            </div>

            {/* <div className="form-group">
             <label>Shift In-charge (POC) Details</label>
             <select
               value={config.pocDetails}
               onChange={(e) => updateConfig("pocDetails", e.target.value)}
             >
               <option value="">Select point of contact</option>
               {fetchContactDetails.map((poc) => (
                 <option
                   key={poc.id}
                   value={`${poc.name} (${poc.email}) ${poc.contact}`}
                 >
                   {poc.name} ({poc.email})
                 </option>
               ))}
             </select>
           </div> */}
          </div>

          <div className="right-column">
            <div className="schedule-config">
              <h3>
                {config.shiftType === "single"
                  ? "1 Day Job Scheduling"
                  : "Multiple Days Job Scheduling"}
              </h3>
              <div className="grid-2-cols">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={config.startDate}
                    min={today}
                    onChange={handleDateChange}
                    onKeyDown={(e) => e.preventDefault()} // Prevent manual entry of invalid dates
                    style={{ 
                      backgroundColor: "#f0f9ff",
                      color: '#333',
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Start Time</label>

                  <select
                    value={config.startTime || ""}
                    onChange={(e) => updateConfig("startTime", e.target.value)}
                    style={{ backgroundColor: "#f0f9ff", height: "36px" }}
                  >
                    <option value="">Select time</option>
                    {timeOptions.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
                {config.shiftType === "multiple" && (
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={config.endDate}
                      min={config.startDate || today}
                      onChange={(e) => updateConfig("endDate", e.target.value)}
                      style={{ 
                        backgroundColor: "#f0f9ff",
                        color: '#333',
                        WebkitAppearance: 'none',
                        appearance: 'none'
                      }}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>
                    No. of Hours ({minimumHour} - {maximumHour})
                  </label>
                  <input
                    type="number"
                    min={minimumHour}
                    max={maximumHour}
                    step="0.5"
                    value={config.duration || ""}
                    onChange={(e) => {
                      const rawValue = e.target.value;

                      // Allow empty string for deletions
                      if (rawValue === "") {
                        updateConfig("duration", "");
                        return;
                      }

                      // const value = parseInt(rawValue, 10);
                      const value = parseFloat(rawValue); 

                      // Allow if within valid range
                      // if (value >= minimumHour && value <= maximumHour) {
                      // updateConfig("duration", value);
                       const isMultipleOfHalf = (value * 10) % 5 === 0;
                       if (isMultipleOfHalf) {
                         updateConfig("duration", value);
                       }
                      // }
                    }}
                    onWheel={(e) => e.target.blur()}
                    onBlur={() => {
                      // const value = parseInt(config.duration, 10);
                      const value = parseFloat(config.duration);
                      if (
                        isNaN(value) ||
                        value < minimumHour ||
                        value > maximumHour
                      ) {
                        updateConfig("duration", ""); // Reset or keep previous valid
                        message.error(
                          `Duration must be between ${minimumHour} and ${maximumHour} hours.`
                        );
                      }
                    }}
                    // onChange={(e) => {
                    //   const value = parseInt(e.target.value) || "";
                    //   if (
                    //     value === "" ||
                    //     (value >= minimumHour && value <= maximumHour)
                    //   )
                    //     updateConfig("duration", value);
                    // }}
                    // placeholder={`Enter number of hours between ${minimumHour} - ${maximumHour}`}
                    placeholder={`Enter duration between ${minimumHour}-${maximumHour} hrs`}
                    style={{ backgroundColor: "#f0f9ff" }}
                  />
                </div>
              </div>
            </div>

            {config.shiftType === "multiple" && (
              <div className="form-group">
                <label>Recurring Schedule Days</label>
                <div className="days-group">
                  {dayLabels.map((day) => (
                    <button
                      key={day}
                      type="button"
                      className={
                        config.recurringDays.includes(day)
                          ? "day active"
                          : "day"
                      }
                      onClick={() => toggleRecurringDay(day)}
                    >
                      {day}
                    </button>
                  ))}
                </div>
                <p className="note">
                  All days selected by default. Click to unselect.
                </p>
              </div>
            )}

            <div className="alert">
              <h3>Shift Summary</h3>
              <p>{summary.totalShifts} Shifts will be posted</p>
              <p>
                Estimated Charges: ₹{summary.estimatedCost.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {onGenerateShifts && (
          <div className="footer">
            <button
              onClick={() => {
                setInitialConfig({ ...config }); // Save current config as base
                setInitialProfessionalDetails({ ...professionalDetails });
                setIsGeneratedOnce(true); // Disable until input changes
                onGenerateShifts(); // Call actual generation handler
              }}
              disabled={isGenerating || isGeneratedOnce}
              className="generate-btn"
            >
              {isGenerating ? "Generating Shifts..." : "Generate Shifts"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftConfiguration;
